// src/hooks/usePermissionFilters.js
// Centralized hook for permission-aware API filter parameters.
// Returns filter objects that scope API calls to only the data
// the current user's permissions allow them to see.

import { useMemo } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';
import { useUser } from '@/contexts/UserContext';

export function usePermissionFilters() {
  const { can, loading } = usePermissions();
  const { user } = useUser();

  const isAdmin = user?.role === 'Admin' || user?.role === 'admin';

  return useMemo(() => {
    const canViewAllScheduling = isAdmin || can('scheduling_view_others');
    const canViewAllClinical = isAdmin || can('clinical_view_all_patients');
    const canViewAllBilling = isAdmin || can('billing_view_all');
    const canAccessBilling = canViewAllBilling || can('billing_manage');
    const canAccessBillingAny = canAccessBilling || can('billing_view_own');
    const canViewAllLocations = isAdmin || can('scheduling_view_all_locations');
    const canViewAllMessages = isAdmin || can('messaging_view_all');
    const canDeleteMessages = isAdmin || can('messaging_delete');
    const canViewAllPatientLocations = isAdmin || can('patients_view_all_locations');

    // Location restriction: user can view others' schedules but only at their assigned locations
    const isLocationRestricted = canViewAllScheduling && !canViewAllLocations;

    const getUserLocationId = () => {
      if (!user) return null;
      // Prefer location_ids array, fall back to single location_id
      if (user.location_ids?.length > 0) return user.location_ids[0];
      return user.location_id || null;
    };

    return {
      // Convenience booleans
      canViewAllScheduling,
      canViewAllClinical,
      canViewAllBilling,
      canAccessBilling,
      canAccessBillingAny,
      canViewAllLocations,
      canViewAllMessages,
      canDeleteMessages,
      canViewAllPatientLocations,
      isLocationRestricted,
      loading,

      // Scheduling: org-scoped or provider-scoped
      getSchedulingFilters: () => {
        if (canViewAllScheduling) {
          return { organization_id: user?.organization_id || user?.organization?.id };
        }
        return { provider_id: user?.id };
      },

      // Clinical: org-scoped or provider-scoped
      getClinicalFilters: () => {
        if (canViewAllClinical) {
          return { organization_id: user?.organization_id || user?.organization?.id };
        }
        return { provider_id: user?.id };
      },

      // Billing: org from auth header (no explicit org_id needed) or provider-scoped
      getBillingFilters: () => {
        if (canViewAllBilling) {
          return {};
        }
        return { provider_id: user?.id };
      },

      // Location filter for location-restricted users
      getLocationFilter: () => {
        if (canViewAllLocations || !canViewAllScheduling) {
          return {};
        }
        const locationId = getUserLocationId();
        return locationId ? { location_id: locationId } : {};
      },

      // Messaging: scoped to participant or org-wide
      getMessagingFilters: () => {
        if (canViewAllMessages) {
          return {};
        }
        return { participant_user_id: user?.id };
      },

      // Patient location: all locations or restricted to user's assigned locations
      getPatientLocationFilters: () => {
        if (canViewAllPatientLocations) {
          return {};
        }
        return user?.location_ids?.length > 0 ? { location_ids: user.location_ids } : {};
      },
    };
  }, [can, user, isAdmin, loading]);
}
